import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import {
  osrpData,
  OSRP_PRIORITIES,
  OSRP_FUNDING_SOURCES,
  OSRP_TIMEFRAMES,
  OSRP_STATUSES,
  OSRP_STATUS_KEY,
  OSRP_STATUS_UPDATED,
  OSRP_STATUS_IMAGE_MAP,
} from '../data/openSpaceRecData';
import './OpenSpaceRec.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

// Per-category color themes (see the OSRP source document's section colors).
// The green/orange --cat-header values are darkened from the source hues so the
// white header title + description clear WCAG 1.4.3 AA (≥4.5:1); the blue header
// already passed. --cat-hover stays a step darker than its --cat-header.
const CATEGORY_THEME = {
  resource: { '--cat-header': '#3f7018', '--cat-hover': '#35600f', '--cat': '#3c7a1c', '--cat-light': '#eef6e2', '--cat-border': '#d3e8b6' },
  community: { '--cat-header': '#a34e0e', '--cat-hover': '#8a420b', '--cat': '#b0560f', '--cat-light': '#fbeadd', '--cat-border': '#f3d3ba' },
  management: { '--cat-header': '#3060b8', '--cat-hover': '#284f98', '--cat': '#2f5aad', '--cat-light': '#e6ecf9', '--cat-border': '#c4d2ee' },
};

function getAllActions(data) {
  const actions = [];
  data.categories.forEach(cat =>
    cat.goals.forEach(goal =>
      goal.objectives.forEach(obj =>
        obj.actions.forEach(a => actions.push(a))
      )
    )
  );
  return actions;
}

function OpenSpaceRec() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sel, setSel] = useState({ priority: new Set(), funding: new Set(), timeframe: new Set(), status: new Set() });
  const [expandedCategories, setExpandedCategories] = usePersistentSet('open-space:expandedCategories', () => new Set(osrpData.categories.map(c => c.id)));
  const [expandedGoals, setExpandedGoals] = usePersistentSet('open-space:expandedGoals', () => {
    const s = new Set();
    osrpData.categories.forEach(c => c.goals.forEach((g, gi) => s.add(`${c.id}-g${gi}`)));
    return s;
  });
  const [expandedObjectives, setExpandedObjectives] = usePersistentSet('open-space:expandedObjectives');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('open-space:overviewExpanded');
  const [actionPlanExpanded, setActionPlanExpanded] = usePersistentFlag('open-space:actionPlanExpanded');
  const [legendExpanded, setLegendExpanded] = usePersistentFlag('open-space:legendExpanded');

  useEffect(() => {
    document.title = 'Open Space & Recreation Plan 2025';
  }, []);

  const allActions = useMemo(() => getAllActions(osrpData), []);

  const dimensions = useMemo(() => [
    { key: 'priority', label: 'Priority', values: OSRP_PRIORITIES.map(v => ({
      value: v, label: `Priority ${v}`, count: allActions.filter(a => a.priority === v).length,
    })) },
    { key: 'funding', label: 'Funding Source', values: OSRP_FUNDING_SOURCES.map(v => ({
      value: v, label: v, count: allActions.filter(a => a.fundingSource && a.fundingSource.includes(v)).length,
    })) },
    { key: 'timeframe', label: 'Timeframe', values: OSRP_TIMEFRAMES.map(v => ({
      value: v, label: v, count: allActions.filter(a => a.proposedCompletion === v).length,
    })) },
    { key: 'status', label: 'Status', values: OSRP_STATUSES.map(v => ({
      value: v, label: v, count: allActions.filter(a => a.status === v).length,
    })) },
  ], [allActions]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ priority: new Set(), funding: new Set(), timeframe: new Set(), status: new Set() });

  const matchesAction = useMemo(() => {
    return (a) => {
      if (sel.priority.size && !sel.priority.has(a.priority)) return false;
      if (sel.funding.size && !(a.fundingSource && [...sel.funding].some(f => a.fundingSource.includes(f)))) return false;
      if (sel.timeframe.size && !sel.timeframe.has(a.proposedCompletion)) return false;
      if (sel.status.size && !sel.status.has(a.status)) return false;
      return true;
    };
  }, [sel]);

  const filteredCategories = useMemo(() => {
    return osrpData.categories.map(category => ({
      ...category,
      goals: category.goals.map(goal => ({
        ...goal,
        objectives: goal.objectives.map(obj => ({
          ...obj,
          actions: obj.actions.filter(matchesAction),
        })).filter(obj => obj.actions.length > 0),
      })).filter(goal => goal.objectives.length > 0),
    })).filter(category => category.goals.length > 0);
  }, [matchesAction]);

  const totalFilteredActions = useMemo(() =>
    filteredCategories.reduce((sum, cat) =>
      sum + cat.goals.reduce((s2, g) =>
        s2 + g.objectives.reduce((s3, o) => s3 + o.actions.length, 0), 0), 0),
    [filteredCategories]);

  const toggleFromSet = (setter) => (key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // Deep link to a goal or an objective: /open-space#resource-protection-g1
  // opens that goal, and #resource-protection-g1-o0 opens the objective inside
  // it. The 2030 Roadmap links here that way, so these keys are part of those
  // URLs — they are "<category>-g<goal index>" and "…-o<objective index>", so
  // reordering the data changes them.
  useEffect(() => {
    const key = decodeURIComponent(location.hash.replace('#', ''));
    const m = key.match(/^([a-z-]+)-g(\d+)(?:-o(\d+))?$/);
    if (!m) return;
    const [, categoryId, goalIdx, objIdx] = m;
    const category = osrpData.categories.find(c => c.id === categoryId);
    const goal = category && category.goals[Number(goalIdx)];
    if (!goal) return;
    setExpandedCategories(prev => new Set(prev).add(categoryId));
    setExpandedGoals(prev => new Set(prev).add(`${categoryId}-g${goalIdx}`));
    if (objIdx !== undefined && goal.objectives[Number(objIdx)]) {
      setExpandedObjectives(prev => new Set(prev).add(key));
    }
    const timer = setTimeout(() => {
      const el = document.getElementById(key);
      if (!el) return;
      el.focus({ preventScroll: true });
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const toggleCategory = toggleFromSet(setExpandedCategories);
  const toggleGoal = toggleFromSet(setExpandedGoals);
  const toggleObjective = toggleFromSet(setExpandedObjectives);

  const expandAll = () => {
    const cats = new Set();
    const goals = new Set();
    const objs = new Set();
    osrpData.categories.forEach(c => {
      cats.add(c.id);
      c.goals.forEach((g, gi) => {
        goals.add(`${c.id}-g${gi}`);
        g.objectives.forEach((o, oi) => objs.add(`${c.id}-g${gi}-o${oi}`));
      });
    });
    setExpandedCategories(cats);
    setExpandedGoals(goals);
    setExpandedObjectives(objs);
    setOverviewExpanded(true);
    setActionPlanExpanded(true);
    setLegendExpanded(true);
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setExpandedGoals(new Set());
    setExpandedObjectives(new Set());
    setOverviewExpanded(false);
    setActionPlanExpanded(false);
    setLegendExpanded(false);
  };

  // A goal or objective may carry a roadmapRef whose text has an authored
  // <a href> into the 2030 Roadmap. Those links are plain HTML the router never
  // sees, so route same-site ones through it.
  const handleRefLinkClick = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };

  const priorityClass = (p) => (p === 1 ? 'osrp-priority-1' : p === 2 ? 'osrp-priority-2' : 'osrp-priority-3');

  const renderActionMeta = (action) => {
    const source = action.fundingSource && action.fundingSource.length ? action.fundingSource.join(', ') : null;
    const hasLevel = action.fundingLevel && action.fundingLevel !== 'N/A' && action.fundingLevel !== '';
    return (
      <div className="osrp-action-meta">
        <span className={`osrp-priority-badge ${priorityClass(action.priority)}`}>P{action.priority}</span>
        {source && (
          <span className="osrp-meta-item"><span className="osrp-meta-label">Funding:</span> {source}{hasLevel ? ` (${action.fundingLevel})` : ''}</span>
        )}
        {action.proposedCompletion && (
          <span className="osrp-meta-item"><span className="osrp-meta-label">By:</span> {action.proposedCompletion}</span>
        )}
        {action.responsibleLead && action.responsibleLead.length > 0 && (
          <span className="osrp-meta-item"><span className="osrp-meta-label">Lead:</span> {action.responsibleLead.join(', ')}</span>
        )}
      </div>
    );
  };

  return (
    <div className="osrp-page">
      <a href="#main-content" className="osrp-skip-link">Skip to main content</a>
      <Header
        title="Open Space & Recreation Plan"
        subtitle="Brookline · 2025"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="osrp-content">
        <div className="osrp-intro">
          <p className="osrp-updated">Source: Brookline Open Space and Recreation Plan {osrpData.planYear}</p>
        </div>

        <div className="osrp-controls">
          <FilterBar
            allCount={allActions.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="osrp-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="osrp-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFilteredActions}
          noun="action"
          nounPlural="actions"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="osrp-preamble-sections">
          <div className="osrp-preamble">
            <button
              className={`osrp-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`osrp-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="osrp-preamble-title">Overview</span>
            </button>
            {overviewExpanded && (
              <div className="osrp-preamble-body">
                {osrpData.introduction.overview.map((para, i) => <p key={i}>{para}</p>)}
              </div>
            )}
          </div>

          <div className="osrp-preamble">
            <button
              className={`osrp-preamble-header ${actionPlanExpanded ? 'expanded' : ''}`}
              onClick={() => setActionPlanExpanded(!actionPlanExpanded)}
              aria-expanded={actionPlanExpanded}
            >
              <span className={`osrp-chevron${actionPlanExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="osrp-preamble-title">About the Ten-Year Action Plan</span>
            </button>
            {actionPlanExpanded && (
              <div className="osrp-preamble-body">
                {osrpData.introduction.actionPlan.map((para, i) => <p key={i}>{para}</p>)}
              </div>
            )}
          </div>

          <div className="osrp-preamble">
            <button
              className={`osrp-preamble-header ${legendExpanded ? 'expanded' : ''}`}
              onClick={() => setLegendExpanded(!legendExpanded)}
              aria-expanded={legendExpanded}
            >
              <span className={`osrp-chevron${legendExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="osrp-preamble-title">How to Read the Actions (Legend)</span>
            </button>
            {legendExpanded && (
              <div className="osrp-preamble-body">
                <div className="osrp-legend-grid">
                  <div className="osrp-legend-block">
                    <h4>Funding Source</h4>
                    <ul>{osrpData.legend.fundingSource.map(x => <li key={x.code}><strong>{x.code}</strong> — {x.label}</li>)}</ul>
                  </div>
                  <div className="osrp-legend-block">
                    <h4>Funding Level</h4>
                    <ul>{osrpData.legend.fundingLevel.map(x => <li key={x.code}><strong>{x.code}</strong> — {x.label}</li>)}</ul>
                  </div>
                  <div className="osrp-legend-block">
                    <h4>Priority</h4>
                    <ul>{osrpData.legend.priority.map(x => <li key={x.code}><strong>{x.code}</strong> — {x.label}</li>)}</ul>
                  </div>
                  <div className="osrp-legend-block">
                    <h4>Responsible Lead</h4>
                    <ul>{osrpData.legend.responsibleLead.map(x => <li key={x.code}><strong>{x.code}</strong> — {x.label}</li>)}</ul>
                  </div>
                </div>
                <div className="osrp-legend-block osrp-status-key-block">
                  <h4>Status</h4>
                  <p className="osrp-status-key-intro">
                    The progress of actions in the OSRP is tracked based on one of five implementation
                    phases. Statuses are from the Town’s Open Space &amp; Recreation Plan web page,
                    last updated {OSRP_STATUS_UPDATED}.
                  </p>
                  <ul className="osrp-status-key">
                    {OSRP_STATUS_KEY.map(x => (
                      <li key={x.code}>
                        <img src={OSRP_STATUS_IMAGE_MAP[x.code]} alt="" className="osrp-status-key-img" />
                        <span><strong>{x.code}</strong> — {x.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="osrp-legend-note">Priority and Proposed Completion do not correlate directly to when progress will begin on any given action. Many actions require steps be taken early in the ten-year timeline to maximize effectiveness.</p>
              </div>
            )}
          </div>
        </div>

        <div className="osrp-categories">
          {filteredCategories.map(category => {
            const catExpanded = expandedCategories.has(category.id);
            return (
              <div
                key={category.id}
                className={`osrp-category osrp-cat-${category.colorKey}`}
                style={CATEGORY_THEME[category.colorKey]}
              >
                <h2 className="osrp-heading"><button
                  className={`osrp-category-header ${catExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={catExpanded}
                >
                  <span className="osrp-cathead-focus">
                    <span className={`osrp-chevron osrp-chevron-light${catExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                    <div className="osrp-category-header-text">
                      <span className="osrp-category-name">{category.name}</span>
                      {category.description && <span className="osrp-category-desc">{category.description}</span>}
                    </div>
                  </span>
                </button></h2>

                {catExpanded && (
                  <div className="osrp-category-body">
                    {category.goals.map((goal, gi) => {
                      const goalKey = `${category.id}-g${gi}`;
                      const goalExpanded = expandedGoals.has(goalKey);
                      return (
                        <div key={goalKey} id={goalKey} tabIndex={-1} className="osrp-goal">
                          <h3 className="osrp-heading"><button
                            className={`osrp-goal-header ${goalExpanded ? 'expanded' : ''}`}
                            onClick={() => toggleGoal(goalKey)}
                            aria-expanded={goalExpanded}
                          >
                            <span className={`osrp-chevron${goalExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                            <span className="osrp-goal-id">Goal {gi + 1}:</span>
                            <span className="osrp-goal-title">{goal.title}</span>
                          </button></h3>

                          {goalExpanded && (
                            <div className="osrp-goal-body" onClick={handleRefLinkClick}>
                              {goal.roadmapRef && (
                                <div className="osrp-roadmap-ref">
                                  <span className="osrp-roadmap-badge">{goal.roadmapRef.badge}</span>
                                  <span dangerouslySetInnerHTML={{ __html: goal.roadmapRef.text }} />
                                </div>
                              )}
                              {goal.objectives.map((obj, oi) => {
                                const objKey = `${category.id}-g${gi}-o${oi}`;
                                const objExpanded = expandedObjectives.has(objKey);
                                return (
                                  <div key={objKey} id={objKey} tabIndex={-1} className="osrp-objective">
                                    <h4 className="osrp-heading"><button
                                      className={`osrp-objective-header ${objExpanded ? 'expanded' : ''}`}
                                      onClick={() => toggleObjective(objKey)}
                                      aria-expanded={objExpanded}
                                    >
                                      <span className={`osrp-chevron${objExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                                      <span className="osrp-objective-title">{obj.title}</span>
                                      <span className="osrp-action-count">
                                        {obj.actions.length} action{obj.actions.length !== 1 ? 's' : ''}
                                      </span>
                                    </button></h4>

                                    {objExpanded && (
                                      <div className="osrp-objective-body">
                                        {obj.roadmapRef && (
                                          <div className="osrp-roadmap-ref">
                                            <span className="osrp-roadmap-badge">{obj.roadmapRef.badge}</span>
                                            <span dangerouslySetInnerHTML={{ __html: obj.roadmapRef.text }} />
                                          </div>
                                        )}
                                        {obj.actions.map((action, ai) => (
                                          <div key={ai} className="osrp-action">
                                            <div className="osrp-action-text">
                                              <div className="osrp-action-main">
                                                <span className="osrp-action-num">{ai + 1}.</span>
                                                <span className="osrp-action-desc">{action.description}</span>
                                              </div>
                                              {renderActionMeta(action)}
                                            </div>
                                            {action.status && (
                                              <span className="osrp-status-indicator">
                                                <img
                                                  src={OSRP_STATUS_IMAGE_MAP[action.status]}
                                                  alt=""
                                                  className="osrp-status-img"
                                                />
                                                <span className="osrp-status-label">{action.status}</span>
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/291/Conservation" target="_blank" rel="noopener noreferrer">
              Conservation Commission<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default OpenSpaceRec;
